import React from "react";
import AppContainer from "./AppContainer";
import { UserProvider } from "../contexts/user";
import moment from "moment";

import {
  Button,
  Col,
  Divider,
  FlexboxGrid,
  Message,
  RadioTile,
  RadioTileGroup,
} from "rsuite";
import usePurchase from "./purchase/hooks/usePurchase";
import BookingMentorPreview from "./purchase/BookingMentorPreview";
import { PriceSubTotal, PriceSummary } from "./purchase/PriceComponents";
import PromoCodeChecker from "./purchase/PromoCodeChecker";

const PurchasePage = ({
  mentor,
  date = null,
  time = null,
  packages,
  packagePlan,
  userId,
  actionPath,
}) => {
  const bookingTime = time;
  const bookingDate = moment(date, "DD/MM/YYYY");

  const {
    selectedPackage,
    pricingDetails,
    handlePromoCode,
    handlePackageChange,
    handleSubmit,
    loading,
    promo,
    setPromo,
  } = usePurchase(packagePlan);

  const handleTileSelection = (selectedId) => {
    const selectedPackage = packages.find((p) => p.id === selectedId);
    handlePackageChange(selectedPackage);
  };

  return (
    <UserProvider>
      <AppContainer>
        <FlexboxGrid justify="center">
          <FlexboxGrid.Item
            as={Col}
            xs={24}
            className="sm:tw-px-4 tw-max-w-4xl"
          >
            <Button
              appearance="link"
              onClick={() => window.history.back()}
              className="tw-my-4 tw-p-0"
            >
              Back
            </Button>

            <h1 className="tw-text-3xl">Checkout</h1>
            <Divider />

            <div className="tw-grid tw-grid-cols-8 tw-gap-4 sm:tw-grid-cols-1 ">
              <div className="tw-col-span-5">
                {mentor && date && time && (
                  <BookingMentorPreview
                    mentor={mentor}
                    bookingDate={bookingDate}
                    bookingTime={bookingTime}
                  />
                )}

                <p className="tw-font-bold">Choose your package</p>
                <RadioTileGroup
                  defaultValue={packagePlan.id}
                  onChange={(value) => handleTileSelection(value)}
                >
                  {packages.map((p) => (
                    <RadioTile
                      key={p.id}
                      label={p.name}
                      value={p.id}
                      className="tw-block"
                    >
                      <div className="tw-flex tw-justify-between">
                        <div>
                          <p>
                            {p.id === 12
                              ? "Multi Session - 3 credits"
                              : "Single Session - 1 credit"}
                          </p>
                          <span
                            className={
                              !!promo?.valid_until ? "tw-line-through" : ""
                            }
                          >
                            Valid for{" "}
                            {moment
                              .duration(p.valid_duration, "days")
                              .humanize()}
                          </span>
                        </div>
                        <div>
                          <p className="tw-text-lg">
                            <b>RM {p.cost}</b>
                          </p>
                        </div>
                      </div>
                    </RadioTile>
                  ))}
                </RadioTileGroup>
              </div>

              <div className="tw-col-span-3 sm:tw-col-span-5 tw-border tw-p-5 tw-rounded-xl tw-border-solid tw-border-gray-200">
                <PriceSummary
                  price={pricingDetails.originalPrice}
                  discountedAmount={pricingDetails.discountedAmount}
                  transactionFee={pricingDetails.transactionFee}
                />
                <Divider />

                {promo && (
                  <Message
                    type="success"
                    className="tw-mb-4"
                    closable
                    onClose={() => {
                      setPromo(null);
                    }}
                  >
                    Woohoo! You just scored <b>{promo.discount}%</b> off with
                    promo code: <b>{promo.code}</b>
                    <br />
                    {!!promo.valid_until && (
                      <span>
                        Credit is valid for{" "}
                        <b>
                          {moment
                            .duration(promo.valid_until, "days")
                            .humanize()}
                        </b>
                      </span>
                    )}
                  </Message>
                )}

                <PromoCodeChecker
                  handlePromoCode={handlePromoCode}
                  selectedPackageId={selectedPackage.id}
                />

                <Divider />

                <PriceSubTotal subTotal={pricingDetails.subTotal} />
                <Button
                  appearance="primary"
                  block
                  disabled={!selectedPackage}
                  onClick={() => handleSubmit(userId, actionPath)}
                  loading={loading}
                >
                  Checkout
                </Button>
                <p className="tw-text-sm tw-pt-2 tw-mb-0">
                  By clicking checkout, you agree that this credit will be
                  active from the date of payment and will be valid for the
                  duration indicated in the credit package. By purchasing you
                  also agree to our{" "}
                  <a href="https://www.futurelab.my/terms-of-service">
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a href="https://www.futurelab.my/privacy">Privacy Policy</a>.
                </p>
              </div>
            </div>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </AppContainer>
    </UserProvider>
  );
};

export default PurchasePage;
